<template>
    <ConfirmDialog
        v-model:visible="visible"
        :header="resolvedTitle"
        :message="resolvedMessage"
        :accept-label="resolvedPrimaryLabel"
        :reject-label="resolvedSecondaryLabel"
        :show-reject="showSecondary"
        @accept="handlePrimary"
        @reject="handleReject"
    />
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccountBillingAnalytics } from '@/core/analytics/hooks/useAccountBillingAnalytics';
import ConfirmDialog from '@/components/common/ConfirmDialog.vue';

const router = useRouter();
const { t } = useI18n();
const analytics = useAccountBillingAnalytics();
const visible = ref(false);
const payload = ref(null);

const resolvedTitle = computed(() => payload.value?.title || t('billing.modal.defaultTitle'));
const resolvedMessage = computed(() => payload.value?.message || t('billing.modal.defaultMessage'));
const resolvedPrimaryLabel = computed(() => payload.value?.primaryLabel || t('billing.modal.primaryLabel'));
const resolvedSecondaryLabel = computed(() => payload.value?.secondaryLabel || t('billing.modal.secondaryLabel'));

// 首次积分发放弹窗仅保留主 CTA，去掉 secondary/Later 按钮
const showSecondary = computed(() => payload.value?.reasonCode !== 'trial_credits_granted');

/**
 * 归一化 redirect 路径为目标路由名称
 * 只允许 dashboard / history / result / upload / home，其余归为 external_or_unknown
 */
const normalizeTargetRoute = (redirect) => {
    if (!redirect) return 'dashboard';
    const path = typeof redirect === 'string' ? redirect : '';
    const segments = path.split('/').filter(Boolean);
    const allowed = ['dashboard', 'history', 'result', 'upload', 'home'];
    for (const segment of segments) {
        if (allowed.includes(segment)) return segment;
    }
    return 'external_or_unknown';
};

// 0.9.6.7：动作语义（reason_code：user_closed / primary_redirect）与「是哪种弹窗」
// （notice_reason_code）分离。此前 useBillingGuard 三处不发 reasonCode ⇒ 弹窗类型恒为
// 兜底值 permission_blocked，事后分不清是哪种弹窗。三个事件共用同一归一化，保证可 join。
const resolveNoticeReasonCode = (detail) => detail?.reasonCode || detail?.code || 'permission_blocked';

const show = (detail = {}) => {
    payload.value = detail;
    visible.value = true;

    // 只取 reasonCode / code / 事件类型归一化值，不上传文案
    const reasonCode = resolveNoticeReasonCode(detail);
    analytics.trackBillingNoticeViewed({
        reasonCode,
        noticeReasonCode: reasonCode,
        targetRouteName: normalizeTargetRoute(detail.redirect),
        moduleName: 'billing',
        sourcePosition: 'billing_notice_modal',
        elementName: 'billing_notice',
        elementType: 'modal',
        contentType: 'billing_notice'
    });
};

/**
 * @param {string} source - 关闭来源：notice_close / notice_overlay / notice_secondary
 */
const handleClose = (source = 'notice_close') => {
    const detail = payload.value;
    visible.value = false;
    payload.value = null;

    // 归一化 cta_type / element_name 到方案稳定枚举：
    // X → notice_close / notice_close；overlay → notice_close / notice_overlay；secondary → notice_secondary / notice_secondary
    const closeCtaType = source === 'notice_secondary' ? 'notice_secondary' : 'notice_close';
    const closeElementName = source === 'notice_overlay' ? 'notice_overlay' : source;

    analytics.trackBillingNoticeActionClicked({
        ctaType: closeCtaType,
        actionStatus: 'cancelled',
        reasonCode: 'user_closed',
        noticeReasonCode: resolveNoticeReasonCode(detail),
        targetRouteName: normalizeTargetRoute(detail?.redirect),
        moduleName: 'billing',
        sourcePosition: 'billing_notice_modal',
        elementName: closeElementName,
        elementType: 'button',
        method: 'user_click'
    });
};

const handlePrimary = () => {
    const target = payload.value?.redirect || '/dashboard';
    const normalizedRoute = normalizeTargetRoute(target);

    analytics.trackBillingNoticeActionClicked({
        ctaType: 'notice_primary',
        actionStatus: 'success',
        reasonCode: 'primary_redirect',
        noticeReasonCode: resolveNoticeReasonCode(payload.value),
        targetRouteName: normalizedRoute,
        moduleName: 'billing',
        sourcePosition: 'billing_notice_modal',
        elementName: 'notice_primary',
        elementType: 'button',
        method: 'user_click'
    });

    visible.value = false;
    payload.value = null;
    router.push(target);
};

/**
 * ConfirmDialog reject 来源：button（secondary 按钮）/ close（X 按钮）/ dismiss（遮罩/Esc）
 */
const handleReject = (source = 'close') => {
    if (source === 'button') {
        handleClose('notice_secondary');
    } else {
        handleClose('notice_close');
    }
};

const handleEvent = (event) => {
    if (!event?.detail) {
        return;
    }
    show(event.detail);
};

defineExpose({
    show,
    handleClose,
    handlePrimary,
    handleReject,
    resolvedTitle,
    resolvedMessage,
    showSecondary
});

onMounted(() => {
    window.addEventListener('billing:notice', handleEvent);
    window.addEventListener('subscription:permission-blocked', handleEvent);
});

onUnmounted(() => {
    window.removeEventListener('billing:notice', handleEvent);
    window.removeEventListener('subscription:permission-blocked', handleEvent);
});
</script>
